import isNull from 'lodash/isNull';
import isNil from 'lodash/isNil';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import reduce from 'lodash/reduce';
import sortBy from 'lodash/sortBy';
import some from 'lodash/some';
import cloneDeep from 'lodash/cloneDeep';

import { ICakeCartLine, ISizeExtraGroupExtraProduct } from '../cart';

const TEXT_LENGTH = [
  { min: -Number.MAX_SAFE_INTEGER, max: 13, length: 30 },
  { min: 14, max: 19, length: 40 },
  { min: 20, max: 29, length: 50 },
  { min: 30, max: Number.MAX_SAFE_INTEGER, length: 60 }
];

export const getDefaultSelection = (
  products: ISizeExtraGroupExtraProduct[],
): ISizeExtraGroupExtraProduct[] => {
  const defaultOption = products.find(({ id }) => id === 0);

  return defaultOption ? [defaultOption] : [];
};

export const getPreselectedSelection = (
  products: ISizeExtraGroupExtraProduct[],
): ISizeExtraGroupExtraProduct[] => {
  const firstProduct = products.find(({ id }) => id !== 0);

  return firstProduct ? [firstProduct] : [];
};

export const applySelectionLimit = (
  group: { maxSelections?: number | null },
  selectedProducts: ISizeExtraGroupExtraProduct[],
): ISizeExtraGroupExtraProduct[] => {
  if (!group.maxSelections) {
    return selectedProducts;
  }

  const limitedProducts = selectedProducts
    .filter(({ id }) => id !== 0)
    .slice(0, group.maxSelections);

  return limitedProducts.length > 0
    ? limitedProducts
    : getDefaultSelection(selectedProducts);
};

export const getExtraProductGroup = (group: any, noChoiceHint: string): any => {
  const clone = cloneDeep(group);

  const items = clone.extraProducts || [];

  if (clone.maxSelections === 1 && clone.type === 'YES_NO') {
    items.unshift({
      price: 0,
      name: noChoiceHint,
      hidePrice: true,
      id: 0
    });
  }

  return {
    helpText: clone.description,
    preselectFirstChoice: clone.preselectFirstChoice,
    hasPicture: clone.hasPicture,
    type: clone.type,
    name: clone.name,
    maxSelections: group.maxSelections,
    id: clone.id,
    items: items,
    priority: clone.priority
  };
};

export const filterAddOnsByApplyOnceValue = (cartLine: ICakeCartLine | null, byApplyOnce = false): ICakeCartLine => {
  const sourceLine = cartLine || {} as ICakeCartLine;
  let { imagesForPrinting, questionsMap, extraProductsMap } = sourceLine;

  if (imagesForPrinting) {
    imagesForPrinting = imagesForPrinting
      .filter(({ applyOnce }) => byApplyOnce ? applyOnce : !applyOnce);
  }

  if (questionsMap) {
    questionsMap = Object.keys(questionsMap)
      .reduce((acc, questionId) => {
        const { applyOnce, answer } = questionsMap[questionId];
        const isAppropriate = byApplyOnce ? applyOnce : !applyOnce;

        if (isAppropriate && answer) {
          acc[questionId] = questionsMap[questionId];
        }
        return acc;
      }, {});
  }

  if (extraProductsMap) {
    extraProductsMap = Object.keys(extraProductsMap).reduce((acc, groupId) => {
      const isAppropriate = extraProductsMap[groupId]
        .filter(({ applyOnce }) => byApplyOnce ? applyOnce : !applyOnce)
        .length > 0;

      if (isAppropriate) {
        acc[groupId] = extraProductsMap[groupId];
      }
      return acc;
    }, {});
  }

  return { ...sourceLine, imagesForPrinting, questionsMap, extraProductsMap };
};

export function calcCountForProductsGroup(params: {
  productCount: number,
  groupCount: number,
  shiftCount: number,
  maxQuantity: number | null,
  minQuantity: number,
  isManual?: boolean,
}): number {
  const {
    productCount,
    shiftCount,
    groupCount,
    maxQuantity,
    minQuantity,
    isManual,
  } = params;

  if (isManual) {
    return _calcManualCountForGroup({
      productCount, shiftCount, groupCount, maxQuantity,
      minQuantity,
    });
  }

  return _calcCountForGroup({
    productCount, shiftCount, groupCount, maxQuantity
  });
}

function _calcCountForGroup(params: {
  productCount, groupCount: number, shiftCount: number, maxQuantity: number | null
}): number {
  const { shiftCount, groupCount, maxQuantity } = params;
  let { productCount } = params;

  if (_hasMaxQuantity(maxQuantity) && shiftCount > 0) {
    const maxToAdd = Math.max(maxQuantity - groupCount, 0);
    productCount += (maxToAdd < shiftCount ? maxToAdd : shiftCount);
  } else {
    productCount += shiftCount;
  }

  return Math.max(productCount, 0);
}

function _calcManualCountForGroup(params: {
  productCount: number, groupCount: number, shiftCount: number,
  maxQuantity: number, minQuantity: number,
}): number {
  const {
    productCount,
    shiftCount,
    groupCount,
    maxQuantity,
    minQuantity,
  } = params;
  const pureCount = groupCount - productCount;

  // Validate against minQuantity - if setting a non-zero value, it must meet minimum
  if (minQuantity && shiftCount > 0 && shiftCount < minQuantity) {
    // If trying to set below minimum, return the current count (no change)
    return productCount;
  }

  if (_hasMaxQuantity(maxQuantity) && maxQuantity < (pureCount + shiftCount)) {
    return Math.max(maxQuantity - pureCount, 0);
  }

  return shiftCount;
}

function _hasMaxQuantity(maxQuantity: number | null): boolean {
  return maxQuantity !== null;
}

export function extendWithMinPrice(sizes) {
  forEach(sizes, (size) => {
    if (size.products && size.products.length > 1) {
      const prices = size.products.filter((p) => !isNull(p.price)).map((p) => p.price);

      if (size.price) {
        prices.push(size.price);
      }

      size.minPrice = Math.min(...prices);
    } else if (size.products && size.products.length === 1) {
      const product = size.products[0];
      size.minPrice = isNil(product.price) ? size.price : product.price;
    }
  });

  return sizes;
}

export function extendWithPriceDiff(variants, cakeSize) {
  forEach(cakeSize.products, (p) => {
    let priceDiff = 0;
    if (!isNull(p.price)) {
      priceDiff = p.price - cakeSize.minPrice;
    } else {
      priceDiff = cakeSize.price - cakeSize.minPrice;
    }

    const extendedVariant = find(variants, { id: p.variant.id });
    if (extendedVariant) {
      extendedVariant.priceDiff = priceDiff;
    }
  });

  return variants;
}

export function getCheapestPrinting(collectionKey: string, collection: any[]) {
  const printings = reduce(collection, function (allPrintings, size) {
    if (size[collectionKey]) {
      allPrintings = allPrintings.concat(size[collectionKey]);
    }
    return allPrintings;
  }, []);

  return sortBy(printings, 'price')[0];
}

export function getMaxLengthForSize(sizeValue) {
  const option = TEXT_LENGTH.filter(function (option) {
    return option.min <= sizeValue && sizeValue <= option.max;
  })[0] || TEXT_LENGTH[1];

  return option.length;
}

export function canHasPrinting(collectionKey: string, cartLineCollection, cakeCollection) {
  if (cartLineCollection) {
    return cartLineCollection[collectionKey].length > 0;
  } else {
    return some(cakeCollection, function (item) {
      return item[collectionKey].length > 0;
    });
  }
}
